import type { Editor, ElementEntry, NodeEntry } from 'slate';
import { Element, Node, Path, Point, Text } from 'slate';

import type { ActiveTab } from './build-active-tabs';
import { arrayEntries } from '../../util';
import {
  getIsRightAlignedTab,
  isBreakingSpace,
  isCheckboxElement,
  isImageElement,
  isLeafVoid,
  isTabElement,
  measureText,
  Queries,
  tokenize,
} from '../internal';
import type {
  CheckboxElement,
  ImageElement,
  PageBreakElement,
  ParagraphElement,
  TabElement,
} from '../types';

export interface BreakLineOptions {
  /** The actual width available to the line. */
  maxLineWidthPx: number;
  /** The content model paragraph from which the line is taken. */
  paragraph: ParagraphElement;
  /** The relative point at which the line should start. */
  startPoint: Point | null;
  /** The positions of each tab stop applicable to the line. */
  tabStopsPx: ActiveTab[];
}

export interface BreakLineResult {
  /** The relative point at which the line should end. */
  endPoint: Point | null;
  /** The widths of each tab element in the line, in the order that they appear. */
  tabWidthsPx: number[];
}

/**
 * Given the start point of a line in a content model paragraph, determine the end point of that
 * line.
 */
export function breakLine(editor: Editor, options: BreakLineOptions): BreakLineResult {
  const { paragraph, startPoint } = options;
  const lineProcessor = new LineProcessor(editor, options);

  for (const [node, path] of Node.nodes(paragraph, {
    from: startPoint?.path,
    pass: ([node]) => isLeafVoid(editor, node),
  })) {
    if (Text.isText(node)) {
      const isSplit = !!startPoint && Path.equals(path, startPoint.path);
      lineProcessor.processText(node, isSplit ? startPoint : { offset: 0, path });
    } else if (Element.isElement(node)) {
      lineProcessor.processElement([node, path]);
    }

    // Stop reading nodes if we've reached a definite end point of the line
    if (lineProcessor.atEndOfLine) break;
  }

  return lineProcessor.finalize();
}

/**
 * Process the content of a line to determine where it should be broken.
 */
class LineProcessor {
  /** The current total width of all content on the line, excluding the current word. */
  private contentWidthPx = 0;
  /** Whether the current word includes a tab element. */
  private currentWordIncludesTab = false;
  /** The current width of the word we're currently reading. */
  private currentWordWidthPx = 0;
  private editor: Editor;
  /**
   * The width claimed by the last right-aligned tab on the line. That tab is resized to fill the
   * line once its content is known, shrinking to zero if need be, so the width it claims here
   * never counts against the space available to the content after it.
   */
  private elasticTabWidthPx = 0;
  /** The final determined end point of the line. */
  private endPoint: Point | null = null;
  /** The last known point at which the line can end. */
  private lastPotentialEndPoint: Point | null = null;
  /** The end of the last encountered text node. */
  private lastTextEndPoint: Point | null = null;
  private options: BreakLineOptions;
  /** The widths of each tab element in the line, in the order that they appear. */
  private tabWidthsPx: number[] = [];

  constructor(editor: Editor, options: BreakLineOptions) {
    this.editor = editor;
    this.options = options;
  }

  /**
   * Process a text node. The given start point denotes the path of the text node relative to the
   * paragraph and, if the text node has already been split from the previous line, the offset in
   * that text node that we should continue reading from.
   */
  public processText(textNode: Text, start: Point) {
    const { text } = textNode;
    let { offset } = start;
    const remainingText = text.slice(offset);

    // Process each token (a word, a partial word, or a space) separately
    for (const token of tokenize(remainingText)) {
      this.processToken(token, textNode, { ...start, offset });
      offset += token.length;

      // Stop reading tokens if we've reached a definite end point of the line
      if (this.atEndOfLine) return;
    }

    // Record the end point of the text node (used for breaking before inline void elements)
    this.lastTextEndPoint = { ...start, offset: text.length };
  }

  /**
   * Process an inline element. We always commit the current word before handling a contentful
   * inline element because we treat each of these elements as denoting the end of a word.
   */
  public processElement([element, path]: ElementEntry) {
    if (Queries.isActivePageBreak(element)) {
      this.commitWord();
      this.processPageBreak([element, path]);
      return;
    }

    if (isImageElement(element)) {
      this.commitWord();
      this.processImage([element, path]);
      return;
    }

    if (isTabElement(element)) {
      this.commitWord();
      this.processTab([element, path]);
      return;
    }

    if (isCheckboxElement(element)) {
      this.commitWord();
      this.processCheckbox([element, path]);
      return;
    }
  }

  /** Determine the final result of breaking the line. */
  public finalize(): BreakLineResult {
    return { endPoint: this.endPoint, tabWidthsPx: this.tabWidthsPx };
  }

  /** Whether we've reached a definite end point of the line. */
  public get atEndOfLine() {
    return this.endPoint !== null;
  }

  // Private processor methods

  /** Process an image element (a drawing or a signature field). */
  private processImage([image, imagePath]: NodeEntry<ImageElement>) {
    const { widthPx } = Queries.getImageDimensions(image);
    this.processFixedWidthVoid(widthPx, imagePath);
  }

  /**
   * Process a page break element. We only end the line at a page break element if the paragraph has
   * content after it. If the page break appears at the end of the paragraph, then there's no need
   * to continue the paragraph on the subsequent page, so we report no line break.
   */
  private processPageBreak([, pageBreakPath]: NodeEntry<PageBreakElement>) {
    if (this.hasContentAfter(pageBreakPath)) this.breakLine(this.getPointAfterPath(pageBreakPath));
  }

  /**
   * Process a tab element. Tabs are treated as part of the subsequent word (meaning they can wrap
   * to the next line if the subsequent word does not fit), and have a dynamic width that advances
   * the line to the next available tab stop.
   *
   * If there's no available tab stop, we break the line either before or after the tab depending on
   * whether we're at the start of the line. If there's a valid end point before the tab, use that.
   * Otherwise, break after the tab and treat it as having a no width (so that it's essentially a
   * line break).
   */
  private processTab([tab, tabPath]: NodeEntry<TabElement>) {
    const nextTabStop = this.options.tabStopsPx.find(tab => tab.position > this.contentWidthPx);

    if (!nextTabStop) {
      if (this.lineHasPotentialEndPoint) return this.breakLine();
      if (this.hasContentAfter(tabPath)) this.breakLine(this.getPointAfterPath(tabPath));
      this.tabWidthsPx.push(0);
      return;
    }

    const tabWidthPx = nextTabStop.position - this.contentWidthPx;
    if (getIsRightAlignedTab(tab)) this.elasticTabWidthPx = tabWidthPx;
    this.tabWidthsPx.push(tabWidthPx);
    this.currentWordWidthPx += tabWidthPx;
    this.currentWordIncludesTab = true;
  }

  /** Process a checkbox element. */
  private processCheckbox([checkbox, checkboxPath]: NodeEntry<CheckboxElement>) {
    this.processFixedWidthVoid(
      Queries.renderedCheckboxSizePx(checkbox.children.at(0) ?? {}),
      checkboxPath
    );
  }

  /**
   * Process an inline void element with a fixed width. If the element fits on the line, we advance
   * the line by its width. Otherwise, we break either before or after the element depending on
   * whether it's at the start of the line and whether there's content after it.
   */
  private processFixedWidthVoid(widthPx: number, path: Path) {
    if (this.fitsOnLine(this.contentWidthPx + widthPx)) {
      this.contentWidthPx += widthPx;
      this.lastPotentialEndPoint = this.getPointAfterPath(path);
      return;
    }

    if (this.lineHasPotentialEndPoint) return this.breakLine();

    if (this.hasContentAfter(path)) this.breakLine(this.getPointAfterPath(path));
  }

  /**
   * Process an individual token of a text node. Whenever we encounter a non-space token, we append
   * its width to the current word, so long as there is room for this token on the current line. We
   * only commit this word when we encounter a breaking space or some other content at which we can
   * break, such as a contentful inline void element. A single word can span multiple text nodes.
   *
   * If we run out of space in the middle of a word, we typically break the line at the start of
   * this word, although there are some cases where we break in the middle of a word.
   */
  private processToken(token: string, textNode: Text, start: Point) {
    const tokenWidth = measureText(this.editor, {
      suppressJustifiedWarning: true,
      text: token,
      textNode,
    });

    this.currentWordWidthPx += tokenWidth;

    /**
     * Commit the current word when we encounter a breaking space. Note that we break after the
     * space, and we don't care whether the width of this space exceeds the maximum width of the
     * line. As per Word, trailing white space is allowed to overflow.
     */
    if (isBreakingSpace(token))
      return this.commitWord({ ...start, offset: start.offset + token.length });

    // If the token fits on the line, continue to the next token or node
    if (this.currentWordFitsOnLine) return;

    /**
     * Otherwise, we know that the current token does not fit, so we must determine where to break
     * the line. If the line prior to the non-fitting word has a point at which we can break, do so
     * (discarding the current word).
     */
    if (this.lineHasPotentialEndPoint) return this.breakLine();

    /**
     * Otherwise, since the current word is at the start of the line and still does not fit, we
     * should break in the middle of the current token, keeping the maximum number of characters
     * that will fit on the line (minimum 1 character).
     */

    // Discard the token from the width of the current word
    this.currentWordWidthPx -= tokenWidth;

    // Re-add as many characters as will fit on the line to the current word
    for (const [charIndex, char] of arrayEntries(token.split(''))) {
      const charWidth = measureText(this.editor, {
        suppressJustifiedWarning: true,
        text: char,
        textNode,
      });

      if (charIndex === 0 || this.fitsOnLine(this.currentWordWidthPx + charWidth)) {
        this.currentWordWidthPx += charWidth;
        continue;
      }

      // Once we reach a non-fitting character, break the line before this character
      this.commitWord({ ...start, offset: start.offset + charIndex });
      this.breakLine();
      return;
    }

    /**
     * Generally, the code should never reach this point, except perhaps in some edge cases where
     * ligatures or floating point error result in the combined width of each character being less
     * than the total width of the token. If this happens, continue to the next token or word.
     */
  }

  // Private queries

  /** Whether the current word fits on the line. */
  private get currentWordFitsOnLine() {
    return this.fitsOnLine(this.contentWidthPx + this.currentWordWidthPx);
  }

  /** Check if a given total width fits on the line, ignoring any width an elastic tab can give up. */
  private fitsOnLine(widthPx: number) {
    return widthPx - this.elasticTabWidthPx <= this.options.maxLineWidthPx;
  }

  /** Get the next valid point after a path relative to the paragraph. */
  private getPointAfterPath(path: Path) {
    for (const [, textPath] of Node.texts(this.options.paragraph, { from: path })) {
      if (Path.isAfter(textPath, path)) return { offset: 0, path: textPath };
    }

    throw new Error(`No point after path [${path}]`);
  }

  /** Whether there is any content after a given path in the paragraph. */
  private hasContentAfter(path: Path) {
    return Node.nodes(this.options.paragraph, { from: path }).some(
      ([node, nodePath]) => Path.isAfter(nodePath, path) && !Queries.nodeIsEmpty(node)
    );
  }

  /** Whether a given point is equal to the start point. */
  private isStart(point: Point) {
    if (this.options.startPoint) return Point.equals(point, this.options.startPoint);
    if (point.offset !== 0) return false;
    const [, firstPath] = Node.first(this.options.paragraph, []);
    return Path.equals(point.path, firstPath);
  }

  /** Whether we've encountered any prior point on the line where it can be broken. */
  private get lineHasPotentialEndPoint() {
    return !!this.lastPotentialEndPoint;
  }

  // Private transforms

  /**
   * Break the line at the given point, or at the last potential end point if no point is specified.
   * To avoid a runtime error, this must only be called without an argument if
   * lineHasPotentialEndPoint is true or if commitWord has definitely been called with a non-start
   * point.
   */
  private breakLine(point?: Point) {
    if (!point && !this.lastPotentialEndPoint)
      throw new Error('Cannot end the line without a valid end point');

    if (this.endPoint) throw new Error('Cannot end the line multiple times');
    this.endPoint = point ?? this.lastPotentialEndPoint;

    // If the current word includes a tab that was never committed, discard its width
    if (this.currentWordIncludesTab) this.tabWidthsPx.pop();
  }

  /**
   * Commit the width of the current word to the line. Called only when it is known that the point
   * after this word is a valid point at which the line may be broken. We only record a potential
   * end point if this point is not at the start of the line; this avoids returning a collapsed
   * line that could cause an infinite loop.
   */
  private commitWord(wordEndPoint = this.lastTextEndPoint) {
    if (wordEndPoint && !this.isStart(wordEndPoint)) this.lastPotentialEndPoint = wordEndPoint;
    this.contentWidthPx += this.currentWordWidthPx;
    this.currentWordWidthPx = 0;
    this.currentWordIncludesTab = false;
  }
}
